// src/components/maintenance/Maintenance.js
import React, { useState, useEffect } from "react";
import { Card, Form, Button, Row, Col, Table } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faHistory,
  faSearch,
  faSpinner,
  faSyncAlt,
  faFileExcel,
} from "@fortawesome/free-solid-svg-icons";
import axios from "axios";
// Import library untuk ekspor Excel
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { popup } from "../../common/popup";

const Maintenance = () => {
  const [maintenanceHistory, setMaintenanceHistory] = useState([]);
  const [filteredHistory, setFilteredHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterLoading, setFilterLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [error, setError] = useState("");
  const [machines, setMachines] = useState([]);
  const [sectors, setSectors] = useState([]);

  // Filter state
  const [startDate, setStartDate] = useState(
    new Date(new Date().setMonth(new Date().getMonth() - 1))
      .toISOString()
      .split("T")[0],
  );
  const [endDate, setEndDate] = useState(
    new Date().toISOString().split("T")[0],
  );
  const [selectedMachine, setSelectedMachine] = useState("");
  const [selectedSector, setSelectedSector] = useState("");

  // Fungsi untuk mendapatkan token
  const getConfig = () => {
    return {
      headers: {
        "x-auth-token": localStorage.getItem("token"),
      },
    };
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const config = getConfig();

        const [historyRes, machinesRes, sectorsRes] = await Promise.all([
          axios.get("/api/maintenance/history", config),
          axios.get("/api/machines", config),
          axios.get("/api/sectors", config),
        ]);

        const normalized = (historyRes.data || []).map((row) => ({
          id: row.id,
          issue: row.issue || null,
          solution: row.solution || null,
          startTime: row.start_time || null,
          endTime: row.end_time || null,
          maintenanceStartTime:
            row.maintenance_start_time ||
            row.repair_start_time ||
            row.start_repair ||
            row.mulai_perbaikan ||
            row.start_time || // fallback
            null,

          machine: {
            id: row.machine_id,
            name: row.machine_name || "-",
            code: row.machine_code || "-",
          },
          sector: {
            id: row.sector_id,
            name: row.sector_name || "-",
          },
        }));

        setMaintenanceHistory(normalized);
        setFilteredHistory(normalized);
        setMachines(machinesRes.data || []);
        setSectors(sectorsRes.data || []);
      } catch (err) {
        console.error("Error fetching data:", err);
        setError("Gagal memuat data");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Client-side filtering
  const applyFilters = () => {
    setFilterLoading(true);

    try {
      console.log("Applying filters with params:", {
        startDate,
        endDate,
        machine: selectedMachine,
        sector: selectedSector,
      });

      // Start with all history data
      let filtered = [...maintenanceHistory];

      // Apply date filters
      if (startDate) {
        const startDateObj = new Date(startDate);
        filtered = filtered.filter((record) => {
          const recordStartTime = new Date(record.startTime);
          return recordStartTime >= startDateObj;
        });
      }

      if (endDate) {
        const endDateObj = new Date(endDate);
        endDateObj.setHours(23, 59, 59, 999); // End of day
        filtered = filtered.filter((record) => {
          const recordStartTime = new Date(record.startTime);
          return recordStartTime <= endDateObj;
        });
      }

      if (selectedMachine) {
        filtered = filtered.filter(
          (record) => Number(record.machine.id) === Number(selectedMachine),
        );
      }
      if (selectedSector) {
        filtered = filtered.filter(
          (record) => Number(record.sector.id) === Number(selectedSector),
        );
      }

      console.log("Filtered results:", filtered);
      setFilteredHistory(filtered);
    } catch (err) {
      console.error("Error applying filters:", err);
    } finally {
      setFilterLoading(false);
    }
  };

  // Reset filters
  const resetFilters = () => {
    setStartDate(
      new Date(new Date().setMonth(new Date().getMonth() - 1))
        .toISOString()
        .split("T")[0],
    );
    setEndDate(new Date().toISOString().split("T")[0]);
    setSelectedMachine("");
    setSelectedSector("");
    setFilteredHistory(maintenanceHistory);
  };

  // Format tanggal
  // eslint-disable-next-line no-unused-vars
  const formatDateTime = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleString("id-ID", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Hitung durasi dalam menit
  const calculateDuration = (startTime, endTime) => {
    if (!startTime || !endTime) return "N/A";
    const duration = Math.round(
      (new Date(endTime) - new Date(startTime)) / 60000,
    );
    return `${duration} menit`;
  };

  // Fungsi untuk mengekspor data ke Excel
  const exportToExcel = async () => {
    if (filteredHistory.length === 0) {
      popup.alert("Tidak ada data untuk diekspor");
      return;
    }

    try {
      setExportLoading(true);

      // 1. Siapkan workbook Excel
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("Riwayat Maintenance");

      // 2. Atur lebar kolom
      worksheet.columns = [
        { header: "No", key: "no", width: 5 },
        { header: "Kode", key: "code", width: 10 },
        { header: "Mesin", key: "machine", width: 20 },
        { header: "Sektor", key: "sector", width: 20 },
        { header: "Keterangan", key: "solution", width: 30 },
        { header: "Tanggal", key: "date", width: 15 },
        { header: "Mulai", key: "startTime", width: 10 },
        { header: "Mulai Perbaikan", key: "maintenanceStart", width: 15 },
        { header: "Selesai", key: "endTime", width: 10 },
        { header: "Durasi", key: "duration", width: 15 },
        { header: "Durasi Maintenance", key: "maintenanceDuration", width: 20 },
        { header: "Status", key: "status", width: 15 },
      ];

      // 3. Tambahkan judul laporan
      worksheet.mergeCells("A1:L1");
      const titleCell = worksheet.getCell("A1");
      titleCell.value = "LAPORAN RIWAYAT MAINTENANCE";
      titleCell.font = { bold: true, size: 16 };
      titleCell.alignment = { horizontal: "center" };

      // 4. Tambahkan informasi filter
      worksheet.mergeCells("A2:L2");
      const filterInfo = worksheet.getCell("A2");
      filterInfo.value = `Periode: ${startDate} sampai ${endDate}${selectedSector ? ` | Sektor: ${sectors.find((s) => String(s.id) === String(selectedSector))?.name || ""}` : ""}${selectedMachine ? ` | Mesin: ${machines.find((m) => String(m.id) === String(selectedMachine))?.name || ""}` : ""}`;
      filterInfo.font = { italic: true };
      filterInfo.alignment = { horizontal: "center" };

      // 5. Spasi sebelum tabel
      worksheet.addRow([]);

      // 6. Buat header tabel
      const headerRow = worksheet.addRow([
        "No",
        "Kode",
        "Mesin",
        "Sektor",
        "Keterangan",
        "Tanggal",
        "Mulai",
        "Mulai Perbaikan",
        "Selesai",
        "Durasi",
        "Durasi Maintenance",
        "Status",
      ]);

      // 7. Styling header tabel
      headerRow.eachCell((cell) => {
        cell.font = { bold: true };
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FFD3D3D3" }, // Light gray
        };
        cell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        };
        cell.alignment = { horizontal: "center", vertical: "middle" };
      });

      // 8. Tambahkan data
      filteredHistory.forEach((record, index) => {
        const row = worksheet.addRow([
          index + 1,
          record.machine?.code || "-",
          record.machine?.name || "N/A",
          record.sector?.name || "N/A",

          record.solution || "N/A",
          record.startTime
            ? new Date(record.startTime).toLocaleDateString("id-ID", {
                day: "2-digit",
                month: "short",
                year: "numeric",
              })
            : "N/A",
          record.startTime
            ? new Date(record.startTime).toLocaleString("id-ID", {
                hour: "2-digit",
                minute: "2-digit",
              })
            : "N/A",
          record.maintenanceStartTime || record.startTime
            ? new Date(
                record.maintenanceStartTime || record.startTime,
              ).toLocaleString("id-ID", {
                hour: "2-digit",
                minute: "2-digit",
              })
            : "N/A",
          record.endTime
            ? new Date(record.endTime).toLocaleString("id-ID", {
                hour: "2-digit",
                minute: "2-digit",
              })
            : "N/A",
          calculateDuration(record.startTime, record.endTime),
          calculateDuration(
            record.maintenanceStartTime || record.startTime,
            record.endTime,
          ),
          record.endTime ? "Selesai" : "Sedang Berlangsung",
        ]);

        // 9. Tambahkan border ke semua cell
        row.eachCell((cell) => {
          cell.border = {
            top: { style: "thin" },
            left: { style: "thin" },
            bottom: { style: "thin" },
            right: { style: "thin" },
          };
        });

        // 10. Style status
        const statusCell = row.getCell(12); // Perubahan indeks kolom dari 11 ke 12 karena ada tambahan kolom Kode
        statusCell.alignment = { horizontal: "center" };
        if (statusCell.value === "Selesai") {
          statusCell.font = { color: { argb: "FF008000" } }; // Green
        } else {
          statusCell.font = { color: { argb: "FFFF0000" } }; // Red
        }
      });

      // 11. Tambahkan ringkasan
      worksheet.addRow([]);
      const summaryRow = worksheet.addRow([
        "Total Record:",
        filteredHistory.length,
      ]);
      summaryRow.getCell(1).font = { bold: true };

      // 12. Tambahkan footer
      worksheet.addRow([]);
      const footerRow = worksheet.addRow([
        `Diekspor pada: ${new Date().toLocaleString("id-ID")}`,
      ]);
      footerRow.getCell(1).font = { italic: true };

      // 13. Ekspor workbook ke file Excel
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      saveAs(
        blob,
        `Riwayat_Maintenance_${new Date().toISOString().split("T")[0]}.xlsx`,
      );

      console.log("Ekspor Excel berhasil");
    } catch (error) {
      console.error("Gagal mengekspor data ke Excel:", error);
      popup.alert("Gagal mengekspor data ke Excel: " + error.message);
    } finally {
      setExportLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center p-5">
        <FontAwesomeIcon
          icon={faSpinner}
          spin
          size="3x"
          className="text-primary mb-3"
        />
        <h5>Memuat data riwayat...</h5>
      </div>
    );
  }

  return (
    <Card>
      <Card.Header>
        <div className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0">Riwayat Maintenance</h5>
          <Button
            variant="success"
            size="sm"
            onClick={exportToExcel}
            disabled={filteredHistory.length === 0 || exportLoading}
          >
            {exportLoading ? (
              <>
                <FontAwesomeIcon icon={faSpinner} spin className="me-1" />{" "}
                Mengekspor...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faFileExcel} className="me-1" /> Ekspor
                ke Excel
              </>
            )}
          </Button>
        </div>
      </Card.Header>
      <Card.Body className="px-0 py-0">
        {/* Filter controls */}
        <div className="p-3 border-bottom bg-light">
          <Row className="align-items-end">
            <Col>
              <Form.Group>
                <Form.Label>Tanggal Mulai</Form.Label>
                <Form.Control
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col>
              <Form.Group>
                <Form.Label>Tanggal Akhir</Form.Label>
                <Form.Control
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col>
              <Form.Group>
                <Form.Label>Sektor</Form.Label>
                <Form.Select
                  value={selectedSector}
                  onChange={(e) => setSelectedSector(e.target.value)}
                >
                  <option value="">Semua Sektor</option>
                  {sectors.map((sector) => (
                    <option key={sector.id} value={sector.id}>
                      {sector.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col>
              <Form.Group>
                <Form.Label>Mesin</Form.Label>
                <Form.Select
                  value={selectedMachine}
                  onChange={(e) => setSelectedMachine(e.target.value)}
                >
                  <option value="">Semua Mesin</option>
                  {machines.map((machine) => (
                    <option key={machine.id} value={machine.id}>
                      {machine.name} {machine.code ? `(${machine.code})` : ""}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md="auto" className="d-flex gap-2">
              <Button
                variant="outline-secondary"
                onClick={resetFilters}
                disabled={filterLoading}
              >
                <FontAwesomeIcon icon={faSyncAlt} />
              </Button>
              <Button
                variant="primary"
                className="d-flex align-items-center"
                onClick={applyFilters}
                disabled={filterLoading}
              >
                {filterLoading ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
                    <span>Memproses...</span>
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faSearch} className="me-2" />
                    <span>Terapkan Filter</span>
                  </>
                )}
              </Button>
            </Col>
          </Row>
        </div>

        {/* Empty state - if no records found after filtering */}
        {filteredHistory.length === 0 && !loading ? (
          <div className="text-center p-5">
            <FontAwesomeIcon
              icon={faHistory}
              size="3x"
              className="text-muted mb-3"
            />
            <h5>Tidak ada riwayat maintenance ditemukan</h5>
            <p className="text-muted">
              Tidak ada data yang cocok dengan filter yang dipilih atau belum
              ada riwayat maintenance yang tercatat.
            </p>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={resetFilters}
            >
              Reset Filter
            </Button>
          </div>
        ) : (
          /* Maintenance history table */
          <div
            className="table-responsive"
            style={{ maxHeight: "380px", overflowY: "auto" }}
          >
            <Table hover className="mb-0">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>KODE</th>
                  <th>MESIN</th>
                  <th>SEKTOR</th>
                  <th>TANGGAL</th>
                  <th>MULAI</th>
                  <th>MULAI PERBAIKAN</th>
                  <th>SELESAI</th>
                  <th>DURASI</th>
                  <th>STATUS</th>
                </tr>
              </thead>
              <tbody>
                {filteredHistory.map((record, index) => (
                  <tr key={record.id || index}>
                    <td>{index + 1}</td>
                    <td>{record.machine?.code || "-"}</td>
                    <td>{record.machine?.name || "N/A"}</td>
                    <td>{record.sector?.name || "N/A"}</td>

                    <td>
                      {record.startTime
                        ? new Date(record.startTime).toLocaleDateString("id-ID")
                        : "N/A"}
                    </td>
                    <td>
                      {record.startTime
                        ? new Date(record.startTime).toLocaleTimeString("id-ID")
                        : "N/A"}
                    </td>
                    <td>
                      {record.maintenanceStartTime || record.startTime
                        ? new Date(
                            record.maintenanceStartTime || record.startTime,
                          ).toLocaleTimeString("id-ID")
                        : "-"}
                    </td>

                    <td>
                      {record.endTime
                        ? new Date(record.endTime).toLocaleTimeString("id-ID")
                        : "-"}
                    </td>
                    <td>
                      {calculateDuration(record.startTime, record.endTime)}
                    </td>
                    <td>
                      <span
                        className={`status-badge bg-primary ${
                          record.endTime ? "selesai" : "sedang-berlangsung"
                        }`}
                      >
                        {record.endTime ? "Selesai" : "Sedang Berlangsung"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

export default Maintenance;
